import type { DeepReadonly, ToRefs } from 'vue'
import { onMounted, onUnmounted, reactive, readonly, toRefs, watch } from 'vue'

import type { Option } from '~/components/VDropdown.vue'
import type { ThreeDEditMode } from '~/entities/model3d'
import type { DrillDataSet } from '~/entities/drillLabel'
import type { DrillDataConfig } from '~/entities/drillHole'
import type { UseThreeDViewer } from '~/composables/useThreeDViewer'
import type { DrillLegendViewModel, NumericalLegend, CategoricalLegend } from '~/entities/drillHole'
import type { DrillDataSetLayerGroup } from '@/composables/useDrillDataSetGroups'

export type Color = { id: string; color: string }
export type SelectedDataSet = { id: number; name: string }

type State = {
  activeConfig: DrillDataConfig | null
  isGradeBinActive: boolean
  selectedGradeBinColor: Color
  selectedDataSet: SelectedDataSet | undefined
}

export type UseDrillHoleSettings = {
  updateCollarConfig: (config: DrillDataConfig) => void
  updateSurveyConfig: (config: DrillDataConfig) => void
  updateAssayConfig: (config: DrillDataConfig) => void
  updateLegendColor: (payload: { id: string; idx: number; newColor: string }) => void
  toggleGradeBinVisibility: () => void
  numericalAssayLegends: ComputedRef<DeepReadonly<DrillLegendViewModel[]>>
  categoricalAssayLegends: ComputedRef<DeepReadonly<DrillLegendViewModel[]>>
} & ToRefs<DeepReadonly<State>>

export function useDrillHoleSettings({
  threeDViewer,
  editMode,
  selectedLegends,
  selectedLegend,
  selectedDrillLayerGroup,
  selectedLayers,
  onDataSetSelected,
}: {
  threeDViewer: UseThreeDViewer
  editMode: Ref<ThreeDEditMode>
  selectedLegends: DeepReadonly<Ref<DrillLegendViewModel[]>>
  selectedLegend: Ref<Option | null>
  selectedDrillLayerGroup: Ref<DrillDataSet | undefined>
  selectedLayers: Ref<any>
  onDataSetSelected: (dataSetId: number) => void
}): UseDrillHoleSettings {
  const defaultConfig = {
    id: 0,
    data_set_id: 0,
    collar: { radius: 5, color: '', visible: true },
    highlight: { radius: 3, color: '', visible: true },
    survey: { radius: 3, color: '', visible: true },
  } as DrillDataConfig

  const state = reactive<State>({
    activeConfig: null,
    isGradeBinActive: false,
    selectedGradeBinColor: { id: '', color: '' },
    selectedDataSet: undefined,
  })

  const numericalAssayLegends = computed(() =>
    selectedLegends.value
      .filter(l => l.type === 'numerical_assay')
      .map(l => ({
        ...l,
        grades: (l as DeepReadonly<NumericalLegend>).grades.map(g => ({
          ...g,
          color: g.color || '',
          id: `${l.id}-${crypto.randomUUID()}`,
        })),
      })),
  )

  const categoricalAssayLegends = computed(() =>
    selectedLegends.value
      .filter(l => l.type === 'categorical_assay')
      .map(l => ({
        ...l,
        categories: (l as DeepReadonly<CategoricalLegend>).categories.map(c => ({
          ...c,
          color: c.color || '',
          id: `${l.id}-${crypto.randomUUID()}`,
        })),
      })),
  )

  watch([() => threeDViewer.currentSceneConfig.value, () => editMode.value], ([sceneConfig, mode]) => {
    if (mode !== 'write' || !sceneConfig) return

    const sceneConfigs = threeDViewer.world.drillDataSetManager?.drillConfigurationManager?.sceneConfigs
    state.activeConfig = _getActiveConfig(sceneConfigs?.[0])
  })

  watch([() => selectedDrillLayerGroup.value, () => selectedLayers.value], ([selectedDrillLayerGroup, selectedLayers]) => {
    const selectedDataSet = _getSelectedDrillLayerGroup(selectedDrillLayerGroup, selectedLayers)
    const dataSetId = selectedDataSet?.id

    if (!dataSetId) return

    const configs = threeDViewer.world.drillDataSetManager?.drillConfigurationManager?.sceneConfigs
    const currentSceneConfig = configs?.find(c => c.data_set_id === dataSetId)

    if (!currentSceneConfig) return

    state.activeConfig = _getActiveConfig(currentSceneConfig)
    state.selectedDataSet = selectedDataSet

    onDataSetSelected(dataSetId)
    updateCollarConfig(state.activeConfig)
    updateSurveyConfig(state.activeConfig)
    updateAssayConfig(state.activeConfig)
  })

  onMounted(() => {
    document.addEventListener('click', handleOutsideClick)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleOutsideClick)
  })

  function handleOutsideClick(event: MouseEvent) {
    const target = event.target as HTMLElement

    const isIgnoredTarget =
      target.closest('#grade-bins-toggle') ||
      target.closest('[data-type="assay-legend"]') ||
      (target instanceof HTMLInputElement && target.type === 'color')

    if (isIgnoredTarget) return

    if (state.isGradeBinActive) {
      state.isGradeBinActive = false
    }
  }

  function updateCollarConfig(config: DrillDataConfig) {
    const dataSetId = config.data_set_id

    state.activeConfig = config
    threeDViewer.world.drillDataSetManager?.drillConfigurationManager?.updateCollarConfig(config)
    threeDViewer.world.drillDataSetManager?.drillHoleManager?.setCollarRadius(dataSetId, config.collar?.radius ?? 5)
    threeDViewer.world.drillDataSetManager?.drillHoleManager?.setCollarVisibility(dataSetId, config.collar?.visible ?? true)
  }

  function updateSurveyConfig(config: DrillDataConfig) {
    const dataSetId = config.data_set_id

    state.activeConfig = config
    threeDViewer.world.drillDataSetManager?.drillConfigurationManager?.updateSurveyConfig(config)
    threeDViewer.world.drillDataSetManager?.drillHoleManager?.setSurveyRadius(dataSetId, config.survey?.radius ?? 5)
    threeDViewer.world.drillDataSetManager?.drillHoleManager?.setSurveyVisibility(dataSetId, config.survey?.visible ?? true)
  }

  function updateAssayConfig(config: DrillDataConfig) {
    const dataSetId = config.data_set_id

    state.activeConfig = config
    threeDViewer.world.drillDataSetManager?.drillConfigurationManager?.updateAssayConfig(config)
    threeDViewer.world.drillDataSetManager?.drillHoleManager?.setAssayRadius(dataSetId, config.highlight?.radius ?? 5)
    threeDViewer.world.drillDataSetManager?.drillHoleManager?.setAssayVisibility(dataSetId, config.highlight?.visible ?? true)
  }

  function updateLegendColor(payload: { id: string; idx: number; newColor: string }) {
    const dataSetId = state.activeConfig?.data_set_id

    threeDViewer.world.drillDataSetManager?.drillLegendManager?.updateLegendColor(payload)
    threeDViewer.world.drillDataSetManager?.drillHoleManager?.setAssayColor(dataSetId, payload)
    threeDViewer.world.needsRender = true
  }

  function toggleGradeBinVisibility() {
    if (state.isGradeBinActive) {
      state.selectedGradeBinColor = { id: '', color: '' }
      state.isGradeBinActive = false
      return
    }

    const legend = selectedLegends.value.find(l => l.id === selectedLegend.value?.id)
    const colors = _getColors()

    if (!legend) return

    const defaultColor = colors.find(c => c.id.includes(legend.id)) || { id: '', color: '' }

    state.selectedGradeBinColor = defaultColor
    state.isGradeBinActive = true
  }

  function _getColors() {
    const numericalColors = numericalAssayLegends.value.flatMap(legend =>
      legend.grades.map(grade => ({ id: grade.id, color: grade.color })),
    )

    const categoricalColors = categoricalAssayLegends.value.flatMap(legend =>
      legend.categories.map(category => ({ id: category.id, color: category.color })),
    )

    return [...numericalColors, ...categoricalColors]
  }

  function _getActiveConfig(currentConfig: DrillDataConfig | undefined): DrillDataConfig {
    if (!currentConfig) return defaultConfig

    return {
      ...currentConfig,
      collar: { ...currentConfig.collar, visible: currentConfig.collar?.visible ?? true },
      survey: { ...currentConfig.survey, visible: currentConfig.survey?.visible ?? true },
      highlight: { ...currentConfig.highlight, visible: currentConfig.highlight?.visible ?? true },
    } as DrillDataConfig
  }

  function _getSelectedDrillLayerGroup(
    selectedDrillLayerGroup: DrillDataSet | undefined,
    selectedLayers: any,
  ): SelectedDataSet | undefined {
    if (selectedDrillLayerGroup) return { id: selectedDrillLayerGroup.data_set_id, name: selectedDrillLayerGroup.data_set.name }

    const selectableLayerTypes = ['drill', 'legend', 'cluster']

    if (!selectedLayers.length || !selectedLayers.some((l: any) => selectableLayerTypes.includes(l.type))) return

    const layerGroup = (threeDViewer.layerGroups.value as DrillDataSetLayerGroup[]).find(g =>
      g.layers.some(l => l.id === selectedLayers[0]?.id),
    )

    if (!layerGroup) return

    return { id: layerGroup.dataSetId, name: layerGroup.name }
  }

  return {
    ...toRefs(readonly(state)),
    updateCollarConfig,
    updateSurveyConfig,
    updateAssayConfig,
    updateLegendColor,
    toggleGradeBinVisibility,
    numericalAssayLegends,
    categoricalAssayLegends,
  }
}
